const AGENT_ERROR_CODE_MAP = {
  AGENT_RATE_LIMITED: '请求过于频繁，请稍后再试',
  AGENT_CONCURRENT_LIMIT: '已有任务在运行，请等待完成',
  AGENT_SESSION_READ_FAILED: '会话读取失败，请刷新页面重试',
  MODEL_NOT_MULTIMODAL: '当前模型不支持图片附件，请切换模型或移除图片',
  ATTACHMENT_READ_ERROR: '附件读取失败，请重新上传',
  AGENT_MESSAGE_PERSIST_FAILED: '消息保存失败，请重试',
  AGENT_DUPLICATE_IN_PROGRESS: '该消息仍在处理中，请稍候',
  AGENT_ERROR: 'Agent 运行失败，请重试',
  AGENT_FINALIZE_ERROR: 'Agent 收尾失败，请重试',
  MESSAGE_NOT_ASSISTANT: '只能重新生成助手消息',
  NO_USER_MESSAGE: '找不到对应的用户消息',
  INVALID_ROLE: '角色不存在或不属于当前用户',
  SKILL_UNAVAILABLE: '技能不存在或已停用',
  AGENT_QUEUE_UNAVAILABLE: 'Agent 排队服务暂不可用，请稍后重试',
  AGENT_QUEUE_FULL: '当前排队任务较多，请稍后再试',
  AGENT_QUEUE_WAIT_TIMEOUT: '排队等待超时，本次未开始执行，请稍后重试',
  SSE_INCOMPLETE: '连接提前中断，回复可能不完整，可点击“重新生成”重试',
};

/** Keeps local page chrome out of the AI Chat composition root. */
export function createPageShellController({
  elements,
  getElement,
  getRoleNameByCode,
  updateMarkExtractButton,
  createToastElement,
  renderHeaderBadge,
  documentRef = document,
  windowRef = window,
  setTimeoutFn = setTimeout,
}) {
  const { sidebar, sidebarScrim, mobileSidebarToggle, workbench, workbenchResizer } = elements;
  let alertResolve = null;
  let bound = false;

  function setMobileSidebarOpen(open) {
    const shouldOpen = Boolean(open);
    sidebar?.classList.toggle('aichat-sidebar--open', shouldOpen);
    if (sidebarScrim) sidebarScrim.hidden = !shouldOpen;
    mobileSidebarToggle?.setAttribute('aria-expanded', String(shouldOpen));
    if (mobileSidebarToggle) mobileSidebarToggle.title = shouldOpen ? '关闭会话列表' : '打开会话列表';
  }

  function closeMobileSidebar() {
    setMobileSidebarOpen(false);
  }

  function showToast(message, type = 'success') {
    const container = getElement('aichat-toasts');
    if (!container) return;
    const toast = createToastElement(message, type);
    container.appendChild(toast);
    setTimeoutFn(() => {
      toast.classList.add('aichat-toast--leaving');
      setTimeoutFn(() => toast.remove(), 300);
    }, 2500);
  }

  function friendlyAgentError(error) {
    if (!error) return 'Agent 出错了，请重试';
    if (error.name === 'AbortError') return '已取消';
    const message = String(error.message || '');
    if (/failed to fetch|networkerror|load failed/i.test(message)) return '网络连接失败，请检查网络后重试';
    if (error.code && AGENT_ERROR_CODE_MAP[error.code]) return AGENT_ERROR_CODE_MAP[error.code];
    if (error.status === 429) return '请求过于频繁，请稍后再试';
    if (error.status === 401 || error.status === 403) return '权限不足或登录已过期，请刷新页面';
    if (error.status === 404) return '请求的资源不存在';
    if (error.status === 408) return '请求超时，请重试';
    if (error.status >= 500 && error.status < 600) return 'AI 服务暂时不可用，请稍后重试';
    if (message && !/^HTTP\s\d+$/.test(message)) return message;
    return 'Agent 出错了，请重试';
  }

  async function copyToClipboard(text, errorMessage = '复制失败') {
    try {
      if (windowRef.navigator?.clipboard && windowRef.isSecureContext) {
        await windowRef.navigator.clipboard.writeText(text);
        return true;
      }
      const textarea = documentRef.createElement('textarea');
      textarea.className = 'aichat-clipboard-helper';
      textarea.value = text;
      documentRef.body.appendChild(textarea);
      textarea.select();
      const copied = documentRef.execCommand('copy');
      documentRef.body.removeChild(textarea);
      if (!copied) throw new Error('execCommand copy failed');
      return true;
    } catch (error) {
      console.warn('[aichat] clipboard copy failed:', error?.message || error);
      showToast(`${errorMessage}，请手动选中复制`, 'error');
      return false;
    }
  }

  function copyCodeBlock(button) {
    const pre = button.closest('.aichat-code-header').nextElementSibling;
    copyToClipboard(pre.textContent, '复制代码失败').then((copied) => {
      if (!copied) return;
      button.textContent = '已复制 ✓';
      setTimeoutFn(() => { button.textContent = '复制'; }, 1500);
    });
  }

  function showConfirm(title, description) {
    return new Promise((resolve) => {
      alertResolve = resolve;
      getElement('aichat-alert-title').textContent = title;
      getElement('aichat-alert-desc').textContent = description;
      getElement('aichat-alert-overlay').classList.add('aichat-show');
    });
  }

  function openLightbox(source) {
    getElement('aichat-lightbox-img').src = source;
    getElement('aichat-lightbox').classList.add('aichat-show');
  }

  function getAvatarClass(code) {
    if (!code) return 'aichat-avatar--general';
    if (code === 'selector_king') return 'aichat-avatar--wang';
    if (code === 'listing_scorer') return 'aichat-avatar--listing';
    if (code === 'ceo_maomao') return 'aichat-avatar--ceo';
    if (code.startsWith('user_')) return 'aichat-avatar--ceo';
    return 'aichat-avatar--general';
  }

  function getAvatarLetter(code, name) {
    if (code === 'selector_king') return '王';
    if (code === 'listing_scorer') return 'L';
    if (code === 'ceo_maomao') return '贸';
    if (name) return name.charAt(0);
    return 'A';
  }

  function updateHeader(session) {
    const header = getElement('aichat-header');
    const title = getElement('aichat-header-title');
    const badge = getElement('aichat-header-badge');
    if (!session) {
      if (header) header.hidden = true;
      updateMarkExtractButton(false);
      return;
    }
    if (header) header.hidden = false;
    if (title) title.textContent = session.title || getRoleNameByCode(session.roleCode) || 'AI 对话';
    renderHeaderBadge(badge, session.model || 'AI');
    updateMarkExtractButton(Boolean(session.skillExtractMark));
  }

  function updateInputVisibility(hasSession) {
    const inputArea = getElement('aichat-input-area');
    if (inputArea) inputArea.hidden = !hasSession;
  }

  function bindWorkbenchResizer() {
    if (!workbenchResizer || !workbench) return;
    let dragging = false;
    let startX = 0;
    let startWidth = 0;
    workbenchResizer.addEventListener('mousedown', (event) => {
      if (workbench.hidden || workbench.classList.contains('aichat-workbench--collapsed')) return;
      dragging = true;
      startX = event.clientX;
      startWidth = workbench.offsetWidth;
      workbenchResizer.classList.add('aichat-workbench-resizer--active');
      documentRef.body?.classList.add('aichat-is-resizing');
      event.preventDefault();
    });
    documentRef.addEventListener('mousemove', (event) => {
      if (!dragging) return;
      const width = Math.min(720, Math.max(280, startWidth + startX - event.clientX));
      workbench.style.setProperty('width', `${width}px`);
      workbench.style.setProperty('flex-basis', `${width}px`);
    });
    documentRef.addEventListener('mouseup', () => {
      if (!dragging) return;
      dragging = false;
      workbenchResizer.classList.remove('aichat-workbench-resizer--active');
      documentRef.body?.classList.remove('aichat-is-resizing');
    });
  }

  function bind() {
    if (bound) return;
    mobileSidebarToggle?.addEventListener('click', () => setMobileSidebarOpen(!sidebar?.classList.contains('aichat-sidebar--open')));
    sidebarScrim?.addEventListener('click', closeMobileSidebar);
    windowRef.addEventListener('resize', () => { if (windowRef.innerWidth > 900) closeMobileSidebar(); });
    getElement('aichat-alert-cancel')?.addEventListener('click', () => { getElement('aichat-alert-overlay').classList.remove('aichat-show'); alertResolve?.(false); });
    getElement('aichat-alert-confirm')?.addEventListener('click', () => { getElement('aichat-alert-overlay').classList.remove('aichat-show'); alertResolve?.(true); });
    getElement('aichat-lightbox')?.addEventListener('click', () => getElement('aichat-lightbox').classList.remove('aichat-show'));
    bindWorkbenchResizer();
    bound = true;
  }

  return { bind, setMobileSidebarOpen, closeMobileSidebar, showToast, friendlyAgentError, copyToClipboard, copyCodeBlock, showConfirm, openLightbox, getAvatarClass, getAvatarLetter, updateHeader, updateInputVisibility };
}
